import { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { TextField, CircularProgress, Box } from "@mui/material";
import { BlankLayout } from "@app/components/layouts";
import { Button, Helmet } from "@app/components/atoms";
import { msalInstance, buildTenantAuthority } from "@app/helpers";
import { apiGetTenantInfo, msalConfig } from "@app/api";
import { startSignup } from "@app/helpers/auth/signup.helper";
import { isWebSignupAllowed } from "@app/helpers/auth/signup-availability.helper";
import { isLogoutRedirectPending } from "@app/hooks";
import { ApiErrorType } from "@app/constants";
import { StorageKey } from "@app/constants";
import "./Login.scss";
import { IconLogo } from "@app/components/icons";
import { PublicClientApplication } from "@azure/msal-browser";
import { useCookies } from "react-cookie";

export function Login() {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSignupAllowed, setSignupAllowed] = useState(false);
  const isSignupDisabled = true;
  const [cookies, , removeCookie] = useCookies([StorageKey.AUTH_ACCESS_TOKEN]);

  const isAlreadyLoggedIn =
    !!cookies?.tokens?.accessToken &&
    !isLogoutRedirectPending() &&
    sessionStorage.getItem("signupInProgress") !== "true";

  useEffect(() => {
    if (isAlreadyLoggedIn) return;

    const clearAllStorage = async () => {
      try {
        try {
          await msalInstance.initialize();
          msalInstance.setActiveAccount(null);
          const localStorageKeys = Object.keys(localStorage);
          const msalKeys = localStorageKeys.filter((key) =>
            key.startsWith("msal."),
          );
          msalKeys.forEach((key) => {
            localStorage.removeItem(key);
          });
        } catch (msalError) {
          console.error("Error clearing MSAL:", msalError);
        }

        localStorage.clear();
        sessionStorage.clear();
        const cookiesList = document.cookie.split(";");
        cookiesList.forEach((cookie) => {
          const eqPos = cookie.indexOf("=");
          const name =
            eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
          if (name) {
            const hostname = window.location.hostname;
            const paths = ["/", window.location.pathname];
            const domains = [
              hostname,
              `.${hostname}`,
              ...(hostname.includes(".")
                ? [
                    hostname.split(".").slice(-2).join("."),
                    `.${hostname.split(".").slice(-2).join(".")}`,
                  ]
                : []),
              "localhost",
            ];
            paths.forEach((path) => {
              document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}`;
              domains.forEach((domain) => {
                document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=${domain}`;
              });
            });
          }
        });
        removeCookie(StorageKey.AUTH_ACCESS_TOKEN, { path: "/" });
      } catch (error) {
        console.error("Error clearing storage:", error);
      }
    };
    clearAllStorage();
  }, [removeCookie, isAlreadyLoggedIn]);

  // useEffect(() => {
  //   let cancelled = false;
  //   isWebSignupAllowed().then((allowed) => {
  //     if (!cancelled) setSignupAllowed(allowed);
  //   });
  //   return () => {
  //     cancelled = true;
  //   };
  // }, []);

  const validateEmail = (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };
  const handleContinue = async () => {
    if (isLoading) return;
    if (!email.trim()) {
      setEmailError("Email is required");
      return;
    }

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setEmailError(null);
    setIsLoading(true);

    try {
      const res = await apiGetTenantInfo(email.trim());
      const tenant = res.data?.data;

      if (!tenant?.clientId || !tenant?.instance || !tenant?.tenantKey) {
        throw new Error("Missing tenant configuration");
      }

      if (tenant.tenantKey === "OA") {
        try {
          await msalInstance.initialize();
          await msalInstance.loginRedirect({
            scopes: [],
            loginHint: email.trim(),
            redirectStartPage: msalConfig.auth.redirectUri,
            extraQueryParameters: { email: email.trim(), prompt: "login" },
          });
          return;
        } catch (error: any) {
          console.error("B2C login error:", error);

          if (
            error.errorCode === "interaction_in_progress" ||
            error.message?.includes("interaction_in_progress")
          ) {
            await msalInstance.handleRedirectPromise();
            setIsLoading(false);
            return;
          }

          throw error;
        }
      }
      const authority = buildTenantAuthority(tenant);
      if (!authority) throw new Error("Unable to determine tenant authority");

      const dynamicMsalConfig = {
        auth: {
          clientId: tenant.clientId,
          authority,
          knownAuthorities: [new URL(tenant.instance).hostname],
          redirectUri: process.env.REACT_APP_REDIRECT_URL,
        },
        cache: {
          cacheLocation: "localStorage",
        },
      };

      localStorage.setItem("authTenant", JSON.stringify(tenant));
      const dynamicInstance = new PublicClientApplication(dynamicMsalConfig);
      await dynamicInstance.initialize();

      const loginRequest = {
        scopes: ["User.Read"],
      };

      await dynamicInstance.loginPopup({
        ...loginRequest,
        loginHint: email,
      });
      window.location.href = "/";
    } catch (err: any) {
      console.error("Login error:", err);

      const apiError = err?.response?.data?.error || err?.response?.data;

      if (
        apiError?.errorType === ApiErrorType.USER_NOT_FOUND ||
        apiError?.message === "Unable to acquire access token" ||
        err?.errorMessage?.includes("AADB2C99002")
      ) {
        const signupAllowed = await isWebSignupAllowed();
        setSignupAllowed(signupAllowed);
        if (!signupAllowed) {
          setEmailError(
            "Login Failed: Account not found. Sign up is not available in your region.",
          );
          setIsLoading(false);
          return;
        }

        try {
          await startSignup(email.trim());
          return;
        } catch {
          setEmailError("Unable to start signup. Please try again.");
          setIsLoading(false);
          return;
        }
      }

      const rawMessage =
        apiError?.message ||
        apiError?.localizedMessage ||
        apiError?.details ||
        "Unable to process login";

      const message =
        rawMessage === "Unable to acquire access token"
          ? "Login Failed: Account not found"
          : rawMessage;

      setEmailError(message);
      setIsLoading(false);
    }
  };
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);

    if (value && !validateEmail(value)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError(null);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleContinue();
    }
  };

  const handleSignup = async (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    setEmailError(null);
    try {
      const signupAllowed = await isWebSignupAllowed();
      setSignupAllowed(signupAllowed);
      if (!signupAllowed) {
        setEmailError("Sign up is not available in your region.");
        setIsLoading(false);
        return;
      }
      await startSignup(email.trim());
    } catch {
      setEmailError("Unable to start signup. Please try again.");
      setIsLoading(false);
    }
  };

  if (isAlreadyLoggedIn) return <Navigate to="/" replace />;

  return (
    <BlankLayout>
      <Helmet title="Login | Operation assist" />
      <div className="login-page">
        <div className="login-left-section">
          <div
            className="login-bg-image"
            style={{ backgroundImage: "url(/bg.jpg)" }}
          ></div>
        </div>
        <div className="login-right-section">
          <div className="login-header-logo">
            <img src="/logo.png" alt="Logo" className="login-logo-img" />
          </div>
          <div className="login-container">
            <div className="login-card">
              <div className="login-logo-container">
                <IconLogo />
              </div>
              <h1 className="login-title">Sign in</h1>
              <p className="login-subtitle">Sign in with your email address</p>

              <div className="login-form">
                <div className="form-group">
                  <TextField
                    fullWidth
                    type="email"
                    label="Email address"
                    value={email}
                    onChange={handleEmailChange}
                    onKeyPress={handleKeyPress}
                    error={!!emailError}
                    helperText={emailError || " "}
                    autoFocus
                    autoComplete="email"
                  />
                </div>

                <Button
                  className="btn-primary login-button"
                  onClick={handleContinue}
                  disabled={!email.trim() || !!emailError || isLoading}
                >
                  {isLoading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress
                        size={20}
                        sx={{ color: "rgba(20, 10, 154, 0.8)" }}
                      />
                      <span>Redirecting...</span>
                    </Box>
                  ) : (
                    "Continue"
                  )}
                </Button>
                {!isSignupDisabled && isSignupAllowed && (
                  <>
                    <div className="login-divider">
                      <span className="divider-line"></span>
                      <span className="divider-text">or</span>
                      <span className="divider-line"></span>
                    </div>

                    <div className="social-login-section">
                      <Link
                        className="signup-link"
                        to=""
                        onClick={handleSignup}
                        aria-disabled={isLoading}
                      >
                        Sign up with a new account
                      </Link>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </BlankLayout>
  );
}
